import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

export interface CommandItem {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly icon: string;
  readonly group: 'Ir para' | 'Ações' | 'Decks';
  readonly keywords?: string;
  readonly run: () => void;
}

interface CommandPaletteProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly items: readonly CommandItem[];
}

function normalize(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('pt-BR');
}

/** Paleta de comandos (⌘K / Ctrl+K): navegação, ações rápidas e salto direto para decks. */
export function CommandPalette({ open, onClose, items }: CommandPaletteProps): React.JSX.Element | null {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const term = normalize(query.trim());
    const filtered = term
      ? items.filter((item) => normalize(`${item.label} ${item.hint ?? ''} ${item.keywords ?? ''}`).includes(term))
      : items;
    return filtered.slice(0, 24);
  }, [items, query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    const fallback = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => { window.cancelAnimationFrame(frame); window.clearTimeout(fallback); };
  }, [open]);

  useEffect(() => { setActiveIndex(0); }, [query]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [open]);

  useEffect(() => {
    const active = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, results]);

  if (!open) return null;

  function select(item: CommandItem): void {
    onClose();
    item.run();
  }

  function onKeyDown(event: React.KeyboardEvent): void {
    if (event.key === 'ArrowDown') { event.preventDefault(); setActiveIndex((index) => Math.min(results.length - 1, index + 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActiveIndex((index) => Math.max(0, index - 1)); }
    else if (event.key === 'Enter') { event.preventDefault(); const item = results[activeIndex]; if (item) select(item); }
    else if (event.key === 'Escape') { event.preventDefault(); onClose(); }
  }

  const groups = (['Ir para', 'Ações', 'Decks'] as const).map((group) => ({ group, entries: results.filter((item) => item.group === group) })).filter((entry) => entry.entries.length > 0);
  let runningIndex = -1;

  return createPortal(
    <div className="command-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="command-palette" role="dialog" aria-modal="true" aria-label="Busca e comandos" onKeyDown={onKeyDown}>
        <label className="command-input">
          <span className="material-symbols-outlined" aria-hidden="true">search</span>
          <input ref={inputRef} value={query} placeholder="Buscar telas, decks ou ações..." aria-label="Buscar telas, decks ou ações" aria-activedescendant={results[activeIndex] ? `command-${results[activeIndex].id}` : undefined} aria-controls="command-results" role="combobox" aria-expanded="true" autoComplete="off" spellCheck={false} onChange={(event) => setQuery(event.target.value)} />
          <kbd>Esc</kbd>
        </label>
        <div className="command-results" id="command-results" role="listbox" ref={listRef}>
          {groups.length === 0 && <p className="command-empty">Nada encontrado para “{query}”.</p>}
          {groups.map(({ group, entries }) => (
            <div className="command-group" key={group} role="group" aria-label={group}>
              <span className="command-group-title">{group}</span>
              {entries.map((item) => {
                runningIndex += 1;
                const index = runningIndex;
                const active = index === activeIndex;
                return (
                  <button className="command-item" type="button" key={item.id} id={`command-${item.id}`} role="option" aria-selected={active} data-active={active} onMouseEnter={() => setActiveIndex(index)} onClick={() => select(item)}>
                    <span className="material-symbols-outlined" aria-hidden="true">{item.icon}</span>
                    <span className="command-item-text"><strong>{item.label}</strong>{item.hint && <small>{item.hint}</small>}</span>
                    {active && <kbd>↵</kbd>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <footer className="command-footer"><span><kbd>↑</kbd><kbd>↓</kbd> navegar</span><span><kbd>↵</kbd> abrir</span><span><kbd>Esc</kbd> fechar</span></footer>
      </div>
    </div>,
    document.body,
  );
}

/** Atalho global: abre a paleta com ⌘K / Ctrl+K e ignora campos de texto para o "/" opcional. */
export function useCommandPaletteShortcut(setOpen: (updater: (open: boolean) => boolean) => void): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);
}

export function useNavigateCommand(): (to: string, state?: unknown) => void {
  const navigate = useNavigate();
  return (to, state) => { void navigate(to, state === undefined ? undefined : { state }); };
}
